import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { createStripeCheckoutSession } from '../../lib/stripeCheckout';
import { getSiteUrl } from '../../lib/siteConfig';
import { supabase } from '../../lib/supabase';
import { useBusiness } from '../../contexts/BusinessContext';

const NEXUS_TEAL = '#21C7D1';
const HORIZON_BLUE = '#3B82F6';
const NEXUS_ICON_SRC = '/nexus-connect-icon.png';

const PREPARING_STEPS = [
  'Creating your workspace…',
  'Configuring Nexus AI…',
  'Installing core apps…',
  'Tuning Growth plan defaults…',
  'Preparing your 10-day trial…',
];

/** Rotating hero titles — synced with each preparing step. */
const HEADLINES = [
  'Building Your CRM',
  'Preparing Smart Scheduling',
  'Activating Marketing Engine',
  'Configuring Customer Hub',
  'Powering Up Growth',
];

const STEP_INTERVAL_MS = 1100;
const HOLD_AFTER_COMPLETE_MS = 700;

type LocationState = {
  userId?: string;
  businessId?: string;
  email?: string;
  fromSignup?: boolean;
};

/**
 * Post-account cinematic beat for signup-nexus:
 * animated "we're building your Nexus" checklist → Start 10-day trial (Growth) → Stripe → Setup.
 */
export default function SignupNexusPreparing() {
  const location = useLocation();
  const { selectedBusiness, forceLoadBusinesses } = useBusiness();
  const state = (location.state || {}) as LocationState;

  const [genStep, setGenStep] = useState(0);
  const [ready, setReady] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const businessId = state.businessId || selectedBusiness?.id || null;
  const email = state.email || null;

  const progress = useMemo(() => {
    if (ready) return 1;
    return Math.min(1, (genStep + 0.35) / PREPARING_STEPS.length);
  }, [genStep, ready]);

  useEffect(() => {
    if (!state.businessId && !selectedBusiness?.id) {
      // Soft recovery: try loading businesses if we landed without state.
      void forceLoadBusinesses?.();
    }
  }, [state.businessId, selectedBusiness?.id, forceLoadBusinesses]);

  useEffect(() => {
    if (ready) return;
    if (genStep >= PREPARING_STEPS.length) {
      const t = window.setTimeout(() => setReady(true), HOLD_AFTER_COMPLETE_MS);
      return () => window.clearTimeout(t);
    }
    const t = window.setTimeout(() => setGenStep((s) => s + 1), STEP_INTERVAL_MS);
    return () => window.clearTimeout(t);
  }, [genStep, ready]);

  const startTrial = async () => {
    setError(null);
    setStarting(true);
    try {
      let bid = businessId;
      let customerEmail = email;

      if (!supabase) throw new Error('Connection unavailable');

      if (!customerEmail) {
        const { data: { user } } = await supabase.auth.getUser();
        customerEmail = user?.email || null;
      }
      if (!bid) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.id) {
          const { data: biz } = await supabase
            .from('businesses')
            .select('id')
            .eq('owner_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          bid = biz?.id || null;
        }
      }

      if (!bid || !customerEmail) {
        throw new Error('Account not ready yet. Please refresh and try again.');
      }

      const site = getSiteUrl();
      const result = await createStripeCheckoutSession({
        planId: 'growth',
        businessId: bid,
        customerEmail,
        successUrl: `${site}/subscription-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${site}/signup-nexus/preparing`,
      });

      if (!result.success) {
        if ('needsBillingPortal' in result && result.needsBillingPortal) {
          throw new Error(result.message || 'Please update billing, then try again.');
        }
        throw new Error('Could not start checkout');
      }

      // createStripeCheckoutSession redirects to Stripe on success; if plan was
      // updated without checkout, send them into the app/setup path.
      if ('planUpdated' in result && result.planUpdated) {
        window.location.href = '/';
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start trial');
      setStarting(false);
    }
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden text-white"
      style={{
        background: '#050507',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
    >
      {/* Atmosphere */}
      <div
        className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[420px] w-[720px] rounded-full opacity-40 blur-3xl"
        style={{
          background: `radial-gradient(ellipse at center, ${NEXUS_TEAL}55 0%, ${HORIZON_BLUE}33 40%, transparent 70%)`,
        }}
      />
      <div
        className="pointer-events-none absolute bottom-0 inset-x-0 h-64 opacity-30"
        style={{
          background: `linear-gradient(to top, ${HORIZON_BLUE}22, transparent)`,
        }}
      />

      <div className="relative z-10 max-w-lg mx-auto px-5 sm:px-6 min-h-screen flex flex-col">
        <div className="py-6 flex justify-center">
          <div className="inline-flex items-center gap-2.5">
            <img
              src={NEXUS_ICON_SRC}
              alt=""
              className="h-9 w-9 sm:h-10 sm:w-10 rounded-[10px] object-cover select-none"
              draggable={false}
              aria-hidden
            />
            <img
              src="/Nexus%20Full%20Logo%20(512%20x%20150%20px).png"
              alt="Nexus"
              className="h-10 sm:h-12 w-auto select-none"
              draggable={false}
            />
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center pb-10">
          {!ready ? (
            <>
              <div className="relative mb-10">
                <div
                  className="w-[88px] h-[88px] rounded-[28px] overflow-hidden flex items-center justify-center shadow-2xl bg-black/40 ring-1 ring-white/10"
                  style={{
                    boxShadow: `0 20px 50px ${HORIZON_BLUE}55`,
                  }}
                >
                  <img
                    src={NEXUS_ICON_SRC}
                    alt="Nexus"
                    className="h-full w-full object-cover"
                    draggable={false}
                  />
                </div>
                <div
                  className="absolute -inset-3 rounded-[34px] border-2 animate-ping opacity-40"
                  style={{ borderColor: NEXUS_TEAL }}
                />
                <div
                  className="absolute -inset-6 rounded-[40px] border opacity-20"
                  style={{ borderColor: HORIZON_BLUE }}
                />
              </div>

              <h1
                key={Math.min(genStep, HEADLINES.length - 1)}
                className="text-[28px] sm:text-[32px] font-bold tracking-tight text-center leading-tight min-h-[2.4em] flex items-center justify-center px-2 animate-[headlineIn_0.45s_ease-out]"
              >
                {HEADLINES[Math.min(genStep, HEADLINES.length - 1)]}
              </h1>
              <p className="mt-3 text-[15px] text-white/55 text-center max-w-sm">
                Hang tight — we&apos;re wiring up the Growth workspace your business will run on.
              </p>

              {/* Progress rail */}
              <div className="mt-8 w-full max-w-xs h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700 ease-out"
                  style={{
                    width: `${Math.round(progress * 100)}%`,
                    background: `linear-gradient(90deg, ${NEXUS_TEAL}, ${HORIZON_BLUE})`,
                  }}
                />
              </div>

              <div className="mt-8 space-y-3 w-full max-w-sm">
                {PREPARING_STEPS.map((label, i) => {
                  const done = i < genStep;
                  const active = i === genStep && genStep < PREPARING_STEPS.length;
                  return (
                    <div
                      key={label}
                      className={`flex items-center gap-3 text-[14px] transition-all duration-500 ${
                        done
                          ? 'text-white/45'
                          : active
                            ? 'text-white font-medium translate-x-0.5'
                            : 'text-white/25'
                      }`}
                    >
                      {done ? (
                        <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: NEXUS_TEAL }} />
                      ) : active ? (
                        <Loader2 className="h-4 w-4 animate-spin shrink-0" style={{ color: HORIZON_BLUE }} />
                      ) : (
                        <div className="h-4 w-4 rounded-full border border-white/20 shrink-0" />
                      )}
                      <span>{label}</span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="w-full max-w-md text-center animate-[fadeInUp_0.55s_ease-out]">
              <h1 className="text-[30px] sm:text-[34px] font-bold tracking-tight leading-tight">
                You&apos;re ready
              </h1>
              <p className="mt-3 text-[16px] text-white/60 leading-relaxed">
                Start your <span className="text-white font-semibold">10-day Growth trial</span> —
                no commitment friction. After checkout, we&apos;ll walk you through setup.
              </p>

              <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-left space-y-1.5">
                {[
                  'Full Growth plan access for 10 days',
                  'Bookings, CRM, marketing, payments & more included',
                  'Then finish a quick guided setup',
                ].map((line) => (
                  <div key={line} className="flex items-start gap-2.5 text-[13px] text-white/70">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" style={{ color: NEXUS_TEAL }} />
                    <span>{line}</span>
                  </div>
                ))}
              </div>

              {error && (
                <p className="mt-4 text-[13px] font-medium text-red-400">{error}</p>
              )}

              <button
                type="button"
                onClick={() => void startTrial()}
                disabled={starting}
                className="mt-8 w-full h-[58px] rounded-full font-semibold text-[17px] text-white flex items-center justify-center gap-2 transition-all disabled:opacity-60 active:scale-[0.99]"
                style={{
                  background: `linear-gradient(135deg, ${NEXUS_TEAL} 0%, ${HORIZON_BLUE} 100%)`,
                  boxShadow: `0 12px 32px ${HORIZON_BLUE}44`,
                }}
              >
                {starting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Opening checkout…
                  </>
                ) : (
                  <>
                    Start 10-day free trial
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>

              <p className="mt-3 text-[13px] text-white/50">
                No credit card required · Growth plan · 10-day trial
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes headlineIn {
          from { opacity: 0; transform: translateY(10px); filter: blur(4px); }
          to { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
      `}</style>
    </div>
  );
}
